import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft, Edit02, User02, XClose } from "@untitled-ui/icons-react";
import { useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAdvanceSearch, useFormError } from "@/hooks";
import { getApiError } from "@/lib/api";
import type { Media, User } from "@/types";
import { useFetchUsers, useFetchUsersInfinite } from "@/user/services";
import { notify } from "@/utils";

import { useCreateGroup } from "../../services";
import MembersPage from "./MembersPage";
import SettingsPage from "./SettingsPage";
import type { FlowPage, GroupPrivacy } from "./types";

const schema = z.object({
  name: z.string().min(1),
  privacy: z.enum(["public", "private"]),
  members: z.array(z.custom<User<true>>()).min(1),
  image: z.custom<Media>().nullable(),
});

export type FormType = z.infer<typeof schema>;

export default function GroupCreateFlow({ open }: { open: boolean }) {
  const { t } = useTranslation("messenger.user");

  const [page, setPage] = useState<FlowPage>();

  const [imagePreview, setImagePreview] = useState<string>();
  const [isImageUploading, setIsImageUploading] = useState(false);

  const searchData = useAdvanceSearch({
    columns: ["first_name", "last_name"],
  });

  const { data: searchUsers } = useFetchUsers(searchData.debounced);

  const {
    data: users,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchUsersInfinite({
    ...searchData.debounced,
    order: "first_name",
    direction: "asc",
  });

  const { watch, setValue, handleSubmit, error } = useFormError({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      privacy: "public" satisfies GroupPrivacy,
      members: [],
      image: null,
    },
  });

  const { mutate: createGroup, isPending } = useCreateGroup();

  const groupName = watch("name");

  const privacy = watch("privacy");

  const members = watch("members");

  const selectedUsers = useMemo(() => members ?? [], [members]);

  const selectedUserIds = useMemo(
    () => new Set(selectedUsers.map((user) => user.id)),
    [selectedUsers],
  );

  const toggleUser = (user: User<true>) => {
    const exists = selectedUsers.some(
      (selectedUser) => selectedUser.id === user.id,
    );

    setValue(
      "members",
      exists
        ? selectedUsers.filter((selectedUser) => selectedUser.id !== user.id)
        : [...selectedUsers, user],
      {
        shouldValidate: true,
      },
    );

    searchData.setQuery("");
  };

  const handleCreateGroupClick = () => {
    setPage("members");
  };

  const handleBack = () => {
    if (page === "settings") {
      setPage("members");
      return;
    }

    setPage(undefined);
  };

  const onSubmit: SubmitHandler<FormType> = (data) => {
    createGroup(
      {
        name: data.name,
        type: data.privacy,
        image: data.image?.uuid ?? null,
        user_ids: data.members.map((user) => user.id),
      },
      {
        onSuccess: () => {
          setPage(undefined);
          notify.success(t("Group has been created successfully"));
        },
        onError: (error) => {
          notify.error(getApiError(error) || t("Failed to create group"));
        },
      },
    );
  };

  const handleActionClick = () => {
    if (page === "members") {
      setPage("settings");
      return;
    }

    if (page === "settings") {
      handleSubmit(onSubmit)();
    }
  };

  if (!open) return null;

  return (
    <>
      {page && (
        <div className="absolute inset-0 z-40 overflow-hidden rounded-xl border-e">
          {page === "members" && (
            <MembersPage
              selectedUsers={selectedUsers}
              selectedUserIds={selectedUserIds}
              users={users}
              searchUsers={searchUsers?.data}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              onBack={handleBack}
              onSearchChange={searchData.setQuery}
              onToggleUser={toggleUser}
              onRemoveUser={toggleUser}
            />
          )}

          {page === "settings" && (
            <SettingsPage
              groupName={groupName}
              privacy={privacy}
              selectedUsers={selectedUsers}
              imagePreview={imagePreview}
              isImageUploading={isImageUploading}
              onBack={handleBack}
              onGroupNameChange={(value) =>
                setValue("name", value, {
                  shouldValidate: true,
                })
              }
              onPrivacyChange={(value) =>
                setValue("privacy", value, {
                  shouldValidate: true,
                })
              }
              onImageUpload={(file) => {
                if (imagePreview) {
                  URL.revokeObjectURL(imagePreview);
                }

                setIsImageUploading(true);
                setImagePreview(URL.createObjectURL(file));
                setIsImageUploading(false);

                setValue("image", null, {
                  shouldValidate: true,
                });
              }}
              onImageDelete={() => {
                if (imagePreview) {
                  URL.revokeObjectURL(imagePreview);
                }

                setImagePreview(undefined);

                setValue("image", null, {
                  shouldValidate: true,
                });
              }}
              groupNameError={error("name")}
            />
          )}
        </div>
      )}

      <div className="absolute end-8 bottom-5 z-50">
        <Popover>
          {({ open: isPopoverOpen, close }) => (
            <>
              <PopoverButton
                className="bg-primary-600 border-primary-600 hover:bg-primary-700 ring-primary-700 rounded-full border p-3 text-white shadow-lg ring-8 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isPending}
                onClick={() => {
                  if (page) {
                    handleActionClick();
                  }
                }}
              >
                {page ? (
                  <ChevronLeft className="size-4" />
                ) : isPopoverOpen ? (
                  <XClose className="size-4" />
                ) : (
                  <Edit02 className="size-4" />
                )}
              </PopoverButton>

              {!page && (
                <PopoverPanel
                  transition
                  anchor={{
                    to: "top end",
                    gap: 8,
                  }}
                  className="origin-bottom-end z-50 w-44 rounded-xl border border-gray-200 bg-white p-1 shadow-lg transition duration-200 ease-in-out data-closed:translate-y-1 data-closed:opacity-0"
                >
                  <div
                    className="flex cursor-pointer items-center px-2.5 hover:bg-gray-50"
                    onClick={() => {
                      close();
                      handleCreateGroupClick();
                    }}
                  >
                    <User02 className="size-4 text-gray-400" />
                    <button
                      type="button"
                      className="flex w-full cursor-pointer items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-700"
                    >
                      {t("New Group")}
                    </button>
                  </div>
                </PopoverPanel>
              )}
            </>
          )}
        </Popover>
      </div>
    </>
  );
}
